import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, X } from 'lucide-react';
import { pierres, filterOptions } from '../mock';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Encyclopedie = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filteredPierres, setFilteredPierres] = useState(pierres);
  const [selectedChakra, setSelectedChakra] = useState('');
  const [selectedIntention, setSelectedIntention] = useState('');
  const [selectedCouleur, setSelectedCouleur] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let results = pierres;

    // Search filter
    if (searchTerm) {
      results = results.filter((pierre) =>
        pierre.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pierre.nomScientifique.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pierre.formuleChimique.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Chakra filter
    if (selectedChakra) {
      results = results.filter((pierre) =>
        pierre.chakras.includes(selectedChakra)
      );
    }

    // Intention filter
    if (selectedIntention) {
      results = results.filter((pierre) =>
        pierre.intentions.includes(selectedIntention)
      );
    }

    // Couleur filter
    if (selectedCouleur) {
      results = results.filter((pierre) =>
        pierre.couleur.toLowerCase().includes(selectedCouleur.toLowerCase())
      );
    }

    setFilteredPierres(results);
  }, [searchTerm, selectedChakra, selectedIntention, selectedCouleur]);

  const resetFilters = () => {
    setSelectedChakra('');
    setSelectedIntention('');
    setSelectedCouleur('');
    setSearchTerm('');
    setSearchParams({});
  };

  const hasActiveFilters = selectedChakra || selectedIntention || selectedCouleur || searchTerm;

  return (
    <main className="encyclopedie-container">
      <div className="encyclopedie-header">
        <h1 className="page-title">Encyclopédie des Pierres</h1>
        <p className="page-subtitle">
          Découvrez nos {pierres.length} pierres répertoriées avec informations scientifiques et propriétés énergétiques.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="controls-section">
        {/* Search Bar */}
        <div className="search-wrapper">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Rechercher par nom, formule chimique..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input-field"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="clear-search-btn"
              aria-label="Effacer la recherche"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Filter Toggle Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="filter-toggle-btn"
        >
          <Filter size={20} />
          Filtres
          {hasActiveFilters && <span className="filter-badge"></span>}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label className="filter-label">Chakra</label>
              <Select value={selectedChakra} onValueChange={setSelectedChakra}>
                <SelectTrigger className="filter-select">
                  <SelectValue placeholder="Tous les chakras" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Tous les chakras</SelectItem>
                  {filterOptions.chakras.map((chakra) => (
                    <SelectItem key={chakra} value={chakra}>{chakra}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Intention</label>
              <Select value={selectedIntention} onValueChange={setSelectedIntention}>
                <SelectTrigger className="filter-select">
                  <SelectValue placeholder="Toutes les intentions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Toutes les intentions</SelectItem>
                  {filterOptions.intentions.map((intention) => (
                    <SelectItem key={intention} value={intention}>{intention}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Couleur</label>
              <Select value={selectedCouleur} onValueChange={setSelectedCouleur}>
                <SelectTrigger className="filter-select">
                  <SelectValue placeholder="Toutes les couleurs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Toutes les couleurs</SelectItem>
                  {filterOptions.couleurs.map((couleur) => (
                    <SelectItem key={couleur} value={couleur}>{couleur}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {hasActiveFilters && (
            <button onClick={resetFilters} className="reset-filters-btn">
              <X size={18} />
              Réinitialiser les filtres
            </button>
          )}
        </div>
      )}

      {/* Results Count */}
      <div className="results-info">
        <p className="results-count">
          {filteredPierres.length} {filteredPierres.length === 1 ? 'pierre trouvée' : 'pierres trouvées'}
        </p>
      </div>

      {/* Pierres Grid */}
      {filteredPierres.length > 0 ? (
        <div className="pierres-grid">
          {filteredPierres.map((pierre) => (
            <Link
              key={pierre.id}
              to={`/pierre/${pierre.id}`}
              className="pierre-card card"
            >
              <div className="pierre-image-container">
                <img src={pierre.image} alt={pierre.nom} className="pierre-image" />
              </div>
              <div className="pierre-details">
                <h3 className="pierre-name">{pierre.nom}</h3>
                <p className="pierre-scientific">{pierre.nomScientifique}</p>
                <p className="pierre-formula">{pierre.formuleChimique}</p>
                <div className="pierre-properties">
                  <span className="property-badge">Mohs: {pierre.dureteMohs}</span>
                  <span className="property-badge color-badge" style={{backgroundColor: getColorCode(pierre.couleur)}}>
                    {pierre.couleur}
                  </span>
                </div>
                <div className="pierre-intentions">
                  {pierre.intentions.slice(0, 3).map((intention, index) => (
                    <span key={index} className="intention-tag">{intention}</span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <p className="no-results-text">Aucune pierre ne correspond à vos critères de recherche.</p>
          <button onClick={resetFilters} className="reset-btn">
            Réinitialiser les filtres
          </button>
        </div>
      )}

      <style jsx>{`
        .encyclopedie-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 3rem 2rem;
          min-height: 80vh;
        }

        .encyclopedie-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .page-title {
          font-size: 3rem;
          color: #1a365d;
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .page-subtitle {
          font-size: 1.15rem;
          color: #4a5568;
          max-width: 600px;
          margin: 0 auto;
        }

        .controls-section {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .search-wrapper {
          flex: 1;
          min-width: 300px;
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 1.25rem;
          color: #718096;
          pointer-events: none;
        }

        .search-input-field {
          width: 100%;
          padding: 1rem 3rem 1rem 3.25rem;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 1rem;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          background: white;
        }

        .search-input-field:focus {
          outline: none;
          border-color: #2d5a8c;
          box-shadow: 0 0 0 3px rgba(45, 90, 140, 0.1);
        }

        .clear-search-btn {
          position: absolute;
          right: 1rem;
          background: none;
          border: none;
          color: #718096;
          cursor: pointer;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          transition: color 0.2s ease;
        }

        .clear-search-btn:hover {
          color: #2d5a8c;
          transform: none;
        }

        .filter-toggle-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 1.75rem;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-weight: 600;
          color: #2d5a8c;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
          position: relative;
        }

        .filter-toggle-btn:hover {
          background: #f7fafc;
          border-color: #2d5a8c;
        }

        .filter-badge {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          width: 8px;
          height: 8px;
          background: #e53e3e;
          border-radius: 50%;
        }

        .filters-panel {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          border: 2px solid #e2e8f0;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 1rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .filter-label {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9rem;
        }

        .reset-filters-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #2d5a8c;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s ease;
          margin-top: 1rem;
        }

        .reset-filters-btn:hover {
          background: #1a365d;
        }

        .results-info {
          margin-bottom: 2rem;
        }

        .results-count {
          color: #4a5568;
          font-weight: 500;
        }

        .pierres-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 2rem;
        }

        .pierre-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          text-decoration: none;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        }

        .pierre-image-container {
          position: relative;
          padding-top: 70%;
          overflow: hidden;
          background: #f7fafc;
        }

        .pierre-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .pierre-card:hover .pierre-image {
          transform: scale(1.08);
        }

        .pierre-details {
          padding: 1.5rem;
        }

        .pierre-name {
          font-size: 1.5rem;
          color: #1a365d;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .pierre-scientific {
          color: #718096;
          font-size: 0.95rem;
          margin-bottom: 0.25rem;
          font-style: italic;
        }

        .pierre-formula {
          color: #4a5568;
          font-family: 'Courier New', monospace;
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }

        .pierre-properties {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .property-badge {
          padding: 0.35rem 0.875rem;
          background: rgba(45, 90, 140, 0.1);
          color: #2d5a8c;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .color-badge {
          color: white;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }

        .pierre-intentions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .intention-tag {
          padding: 0.3rem 0.75rem;
          background: rgba(74, 123, 167, 0.1);
          color: #2d5a8c;
          border-radius: 16px;
          font-size: 0.8rem;
        }

        .no-results {
          text-align: center;
          padding: 4rem 2rem;
        }

        .no-results-text {
          font-size: 1.15rem;
          color: #718096;
          margin-bottom: 1.5rem;
        }

        .reset-btn {
          padding: 0.875rem 2rem;
          background: #2d5a8c;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .reset-btn:hover {
          background: #1a365d;
        }

        @media (max-width: 768px) {
          .encyclopedie-container {
            padding: 2rem 1.5rem;
          }

          .page-title {
            font-size: 2rem;
          }

          .controls-section {
            flex-direction: column;
          }

          .search-wrapper {
            min-width: 100%;
          }

          .filter-toggle-btn {
            width: 100%;
            justify-content: center;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }

          .pierres-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </main>
  );
};

// Helper function for color codes
const getColorCode = (couleur) => {
  const colorMap = {
    'Violet': '#9b59b6',
    'Rose': '#ec7c9b',
    'Jaune': '#f1c40f',
    'Vert': '#27ae60',
    'Bleu': '#3498db',
    'Rouge': '#e74c3c',
    'Orange': '#e67e22',
    'Noir': '#2c3e50',
    'Blanc': '#ecf0f1',
    'Gris': '#95a5a6',
    'Brun': '#8b6f47',
    'Multicolore': 'linear-gradient(90deg, #e74c3c, #f1c40f, #27ae60, #3498db, #9b59b6)'
  };
  return colorMap[couleur] || '#718096';
};

export default Encyclopedie;